/**
 * `/register`: the first page of web self-registration, the "Open a new account" form
 * (`components/Register/Register.tsx`). Reached from the login page's "Create account" button
 * (`components/Login/CreateAccountForm.tsx`) and from the "Cancel" link on `/register/verify`.
 *
 * Registration runs over these routes, each passing its data to the next through
 * `@ryerson/frontend.next` → `navigate(path, { state })`, which writes it to sessionStorage under
 * `page-nav-params-<path>`, and `usePageParams()`, which reads it back:
 * 1. `/register`: name, e-mail, company and postal code, plus an invisible reCAPTCHA, posted to the
 *    Users API `public/registration/send-token`, which e-mails a code
 *    (`components/Register/RegisterForm.tsx`).
 * 2. `/register/verify`: the e-mailed code, checked by `public/registration/validate-token`
 *    (`components/Register/VerifyToken/RegisterVerifyTokenRoot.tsx`). An e-mail that already has an
 *    account is sent to `/login` instead.
 * 3. `/register/create-account`: a password, posted to `public/user/create`, then the new user is
 *    signed in (`components/Register/CreateAccount/CreateAccountRoot.tsx`).
 * 4. `/register/thank-you`: links into the store
 *    (`components/Register/AccountConfirmation/AccountConfirmationRoot.tsx`).
 *
 * `/register/created` (the address step, `components/Register/UserAddress/`) is still built, but no
 * step navigates to it.
 *
 * A visitor who is already signed in is redirected by
 * `components/Login/hooks/useRedirectIfLoggedIn.ts` (to the stored `redirectTo`, otherwise
 * `/store/dashboard` in production) and the page renders nothing. Steps 2 and 3 redirect back here
 * when their state is missing (`components/Login/hooks/useRedirectToRegisterIfNoUser.ts`).
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead from "@components/Shared/model/MetaHead";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import useRedirectIfLoggedIn from "@components/Login/hooks/useRedirectIfLoggedIn";
import Register from "@components/Register/Register";
import { RegisterMetaObject } from "@content/shared/register";

/** Fetches only the Contentful header, locations and footer data that `HeaderLayout` needs. */
export const getStaticProps = async () => {
	return await allContentfulHeaderCalls();
};

/** The `/register` page; see the file header. Its meta tags come from `content/shared/register`. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	const shouldRedirectIfLoggedIn = useRedirectIfLoggedIn();

	return shouldRedirectIfLoggedIn ? null : (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={RegisterMetaObject[language]} />
			<Register />
		</HeaderLayout>
	);
};

export default Page;
